import { Box, Button, Typography } from '@mui/material';
import parse from 'html-react-parser';
import { useCallback, useMemo, useRef } from 'react';

import SenderHeader from '../common/SenderHeader';

import InitialMessage from './InitialMessage';
import { BotMessageProps } from './BotMessage.interface';

import CollectiveIcon from 'assets/img/collective_icon_white.svg';
import { BOT_AUTHOR_ID, BOT_CUSTOM_FIELD_ID, BOT_CUSTOM_FIELD_VALUES, BOT_NAME } from 'services/help-center/constants';
import useSendMessage from 'services/help-center/mutations/useSendComment';
import useTicket from 'services/help-center/queries/useTicket';
import sanitizeHtml from 'modules/common/utils/sanitizeHtml';

const BOT_RESPONSE = {
    [BOT_CUSTOM_FIELD_VALUES.HELPFUL]: {
        message: `<div>
            <p>Glad we could help. Take care!</p>
          </div>`,
        status: 'closed',
    },
    [BOT_CUSTOM_FIELD_VALUES.NOT_HELPFUL]: {
        message: `<div>
            <p>Got it, hang tight. We'll get back to you within a business day.</p>
         </div>`,
        status: 'open',
    },
};

function FeedbackButton({ label, isSelected, onClick }: { label: string; isSelected: boolean; onClick: () => void }) {
    return (
        <Button variant="contained" disabled={isSelected} onClick={onClick}>
            {label}
        </Button>
    );
}

function FeedbackActions({
    botResponseStatus,
    onActionClick,
}: {
    botResponseStatus: string | undefined | null;
    onActionClick: (value: string) => void;
}) {
    if (botResponseStatus === BOT_CUSTOM_FIELD_VALUES.NO_SUGGESTIONS) {
        return null;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1,
                paddingTop: 4,
            }}>
            {botResponseStatus !== BOT_CUSTOM_FIELD_VALUES.NOT_HELPFUL && (
                <FeedbackButton
                    label="Thanks, I don't need any more help"
                    isSelected={botResponseStatus === BOT_CUSTOM_FIELD_VALUES.HELPFUL}
                    onClick={() => onActionClick(BOT_CUSTOM_FIELD_VALUES.HELPFUL)}
                />
            )}
            {botResponseStatus !== BOT_CUSTOM_FIELD_VALUES.HELPFUL && (
                <FeedbackButton
                    label="I still need help"
                    isSelected={botResponseStatus === BOT_CUSTOM_FIELD_VALUES.NOT_HELPFUL}
                    onClick={() => onActionClick(BOT_CUSTOM_FIELD_VALUES.NOT_HELPFUL)}
                />
            )}
        </Box>
    );
}

export default function BotMessage({ initialMessage, botMessage, ticketId }: BotMessageProps) {
    const { mutate: sendMessage } = useSendMessage();
    const { data, isLoading } = useTicket(ticketId);
    const ticket = data?.ticket;
    const boxRef = useRef<HTMLDivElement>(null);

    const handleActionClick = useCallback(
        (value: string) => {
            if (!ticket) {
                return;
            }

            sendMessage({
                ticketId: ticket.id,
                message: BOT_RESPONSE[value].message,
                otherPayload: {
                    status: BOT_RESPONSE[value].status,
                    custom_fields: [{ id: BOT_CUSTOM_FIELD_ID, value }],
                },
                authorId: BOT_AUTHOR_ID,
            });
        },
        [sendMessage, ticket]
    );

    const botResponseStatus = useMemo(
        () => ticket?.custom_fields?.find((field) => field.id === BOT_CUSTOM_FIELD_ID)?.value,
        [ticket]
    );

    if (isLoading) {
        return null;
    }

    return (
        <Box sx={{ paddingY: 2.5, paddingX: 3 }}>
            <SenderHeader
                sender={{
                    id: BOT_AUTHOR_ID,
                    name: BOT_NAME,
                    photo: {
                        content_url: CollectiveIcon,
                    },
                }}
                timestamp={botMessage.created_at ?? ''}
            />
            <Box sx={{ pl: 4 }}>
                <InitialMessage initialMessage={initialMessage} />
                <Typography
                    ref={boxRef}
                    component="div"
                    variant="body2"
                    fontWeight={400}
                    sx={{
                        '& .collapse-signature, & .signature': { display: 'none' },
                        '& .zd-comment > div > p > a': {
                            textDecoration: 'none',
                            marginTop: 3,
                            fontWeight: 600,
                            display: 'block',
                        },
                    }}>
                    {parse(sanitizeHtml(botMessage.html_body ?? ''))}
                </Typography>
                <FeedbackActions botResponseStatus={botResponseStatus} onActionClick={handleActionClick} />
            </Box>
        </Box>
    );
}
